import { Component, computed, DestroyRef, inject, OnInit } from "@angular/core";

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { TranslocoService } from "@jsverse/transloco";
import { isPropertyDefined } from "@recursyve/nice-ts-utils";
import { filter, switchMap, tap } from "rxjs";
import { sanitizeDirectusArray } from "../../../common/utils/sanitize-directus-object";
import { SeoService } from "../../services/seo.service";
import { DynamicPageComponent } from "../components/dynamic-page/dynamic-page.component";
import { PageService } from "./store/page.service";
import { PageStore } from "./store/page.store";

@Component({
    selector: "packbo-page",
    templateUrl: "./page.template.html",
    providers: [PageService, PageStore],
    imports: [DynamicPageComponent]
})
export class PageComponent implements OnInit {
    private readonly pageService = inject(PageService);
    private readonly pageStore = inject(PageStore);
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly seoService = inject(SeoService);
    private readonly router = inject(Router);
    private readonly transloco = inject(TranslocoService);

    public page = this.pageStore.page;
    public blocks = computed(() => sanitizeDirectusArray(this.page()?.blocks_refs));

    public ngOnInit(): void {
        this.route.params
            .pipe(
                filter((params) => isPropertyDefined(params, "slug")),
                switchMap(({ slug }: Params) => this.pageService.loadPage(slug)),
                tap(() => this.updateSeoTags()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.transloco.langChanges$
            .pipe(
                tap(() => this.updateSeoTags()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private updateSeoTags(): void {
        const page = this.page();
        if (page) {
            this.seoService.updateMetaTags(
                {
                    translations: sanitizeDirectusArray(page.translations_refs),
                    noIndex: page.no_index ?? false,
                    noFollow: page.no_follow ?? false,
                    isPrivate: false
                },
                this.router.url
            );
        }
    }
}
